import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  EndSeasonAudit,
  EndSeasonResponse,
  SeasonApiRecord,
  SeasonsListResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntity } from "./responseMappers";

export type EndSeasonVariables = {
  seasonId: string;
  /** Admin-entered audit fields from the confirmation modal (FE-2247).
   *  Absent on the legacy single-click confirmation path (FE-2261 flag-off),
   *  which posts without an audit body like the pre-FE-2247 endpoint call. */
  audit?: EndSeasonAudit;
};

const buildEndSeasonUrl = (seasonId: string): string =>
  API_ROUTES.END_SEASON.replace("{season_id}", seasonId);

const postEndSeason = async ({
  seasonId,
  audit,
}: EndSeasonVariables): Promise<SeasonApiRecord> => {
  const response = await getPrivateInstance().post<EndSeasonResponse>(
    buildEndSeasonUrl(seasonId),
    // FE-2247: audit-trail fields promised by the confirmation modal.
    // Snake_case anticipates the BE audit contract — revisit the field
    // names if the published contract differs (BE ignores unknown body
    // fields on this endpoint today).
    audit
      ? {
          audit: {
            first_name: audit.firstName,
            last_name: audit.lastName,
            takedown_date: audit.takedownDate,
            submitted_at: new Date().toISOString(),
          },
        }
      : {},
  );
  return unwrapGamePassEntity(response.data, "season");
};

export const useEndSeason = (): UseMutationResult<
  SeasonApiRecord,
  Error,
  EndSeasonVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "seasons", "end"],
    mutationFn: postEndSeason,
    onSuccess: (season) => {
      queryClient.setQueriesData<SeasonsListResponse>(
        { queryKey: ["game-pass", "seasons", "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            seasons: current.seasons.map((record) =>
              (record.id ?? record.season_id) === (season.id ?? season.season_id)
                ? { ...record, ...season }
                : record,
            ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
